import { useCallback, useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { useForm } from "react-hook-form";

import { createColumnHelper, RowModel, SortingState } from "@tanstack/react-table";

import Modal from "../../layouts/Modal";
import { PageMode } from "../../models/PagedTable";
import { IPart } from "../../models/Part";
import PagedTable from "../shared/Tables/PagedTable";
import { FormatType, IEbayOffer } from "../../models/EbayOffer";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import usePartMutations from "../../data/usePartMutations";
import { computeEbayListingTitle } from "./EbayListingTitle";
import useNotification from "../notifications/useNotifications";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useBatchListingsMutations from "../../data/useBatchListingsMutations";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import BulkEditListingSpecifics from "./BulkEditListingSpecifics";
import DateRangeFilter from "../shared/DateRangeFilter";
import toIsoDate from "../../utils/toIsoDate";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import { useQueryClient } from "@tanstack/react-query";
import { IWarehouseLocation } from "../../models/Location";
import ListingExpandedRowContent from "./ListingExpandedRowContent";
import SyncEbayListingsModal from "./SyncEbayListingsModal";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import UnlistedPartTypeCell from "./unlistedTable/UnlistedPartTypeCell";
import UnlistedImageCell from "./unlistedTable/UnlistedImageCell";
import UnlistedOfferPrice from "./unlistedTable/UnlistedOfferPrice";
import PriceHistoryButton from "./PriceHistoryButton";
import ListingRecommendationsCell from "./ListingRecommendationsCell";
import { computeListingScore } from "./listedTable/listingScore";
import UnlistedEbayTitle from "./unlistedTable/UnlistedEbayTitle";
import WilmaTitleRow from "./WilmaTitleRow";
import SendToQcModal from "../stock/SendToQcModal";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import { IBulkIntelligencePricingResult, ISerpApiSearchResult } from "../../models/PartLookup";
import BestOfferResetModal from "./listedTable/BestOfferResetModal";
import useOfferUpdate from "../../data/useOfferUpdate";
import BulkUpdatePoliciesModal, { IBulkUpdatePolicies } from "./listedTable/BulkUpdatePoliciesModal";
import BulkRelistResultModal, { BulkRelistResultData } from "./BulkRelistResultModal";
import TableFilterPanel from "../shared/Tables/TableFilterPanel";
import TableDateSortControl, { CREATED_DATE_SORT_ID } from "../shared/Tables/TableDateSortControl";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import classNames from "../../utils/classNames";
import { MISSING_FIELD_OPTIONS } from "./listedTable/missingFields";
import VehiclePartSearchFilters, {
  buildVehiclePartSearchBody,
  defaultVehicleDetailFilterEnabled,
  IVehiclePartSearchFields,
  VehicleDetailFilterKey,
  vehiclePartSearchDefaultValues,
} from "../stock/VehiclePartSearchFilters";

const colHelper = createColumnHelper<IPart>();

// Stable reference so the additionalBody effect doesn't re-run every render when the prop is omitted
const noMissingFields: string[] = [];

type LocationSearchMode = "equals" | "beginsWith";

interface IFilterFormFields extends IVehiclePartSearchFields {
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
}

const defaultFilterFormValues: IFilterFormFields = {
  ...vehiclePartSearchDefaultValues,
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created"
};

// Only the filters owned by this component are saved - the missing field chips live on the
// page (shared with the stats tiles), so they stay out of the saved defaults.
interface IListingsSavedDefaultFilters {
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  startDate: string | null;
  endDate: string | null;
  dateFieldType: string;
  // Optional so filters saved before the sort control existed still apply
  sorting?: SortingState;
}

const defaultSorting: SortingState = [{ id: CREATED_DATE_SORT_ID, desc: true }];

interface IEbayListingsTableProps {
  selectedMissingFields?: string[];
  onToggleMissingField?: (key: string) => void;
}

export default function EbayListingsTable({ selectedMissingFields = noMissingFields, onToggleMissingField }: IEbayListingsTableProps = {}) {
  const { addNotification } = useNotification();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { bulkWithdraw, bulkUpdateOffer, scrapOutPart } = usePartMutations();
  const { bulkRelist } = useBatchListingsMutations();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategoriesPartial();
  const queryClient = useQueryClient();

  // Empty string partId — this hook is used here only for the bulk variant which takes its own ids
  const { intelligencePricingBulk } = usePartsLookupMutations("");

  // Separate loading flag because intelligencePricingBulk.isLoading resets between chunks
  const [intelligenceRunning, setIntelligenceRunning] = useState(false);
  // Snapshot of pricing changes so "Undo Pricing" can restore the exact previous values
  const [pricingUndo, setPricingUndo] = useState<IPricingChange[] | null>(null);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmFunction, setConfirmFunction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");

  const [policyChangeOpen, setPolicyChangeOpen] = useState<boolean>(false);
  const [aspectsChangeOpen, setAspectsChangeOpen] = useState<boolean>(false);
  const [syncEbayOpen, setSyncEbayOpen] = useState<boolean>(false);
  const [qcOpen, setQcOpen] = useState<boolean>(false);
  const [bestOfferOpen, setBestOfferOpen] = useState<boolean>(false);
  // Bulk relist result modal - shows which parts relisted successfully and why the rest didn't
  const [relistResult, setRelistResult] = useState<BulkRelistResultData | null>(null);
  const [relistResultOpen, setRelistResultOpen] = useState<boolean>(false);
  // Dedicated relist confirm (instead of the generic one) so it can carry the Append SKU option
  const [relistConfirmOpen, setRelistConfirmOpen] = useState<boolean>(false);
  const [relistAppendSku, setRelistAppendSku] = useState<boolean>(false);
  const [relistRows, setRelistRows] = useState<RowModel<IPart> | null>(null);
  const [selectedRows, setSelectedRows] = useState<RowModel<IPart> | null>(null);

  // Bulk offer updates run in the background; per-row status rides along on the listings query.
  const { resetBestOffer, updateTitles, updatePolicies } = useOfferUpdate();

  // Full offer snapshot stored so undo can send the original offer object back verbatim
  interface IPricingChange {
    partId: string;
    offer: IEbayOffer;
    isAuction: boolean;
    oldPrice: number;
    newPrice: number;
  }

  const { register: registerFilters, setValue: setFilterValue, watch: watchFilters, getValues: getFilterValues, reset: resetFilters } = useForm<IFilterFormFields>({
    defaultValues: defaultFilterFormValues,
  });

  // The category/vehicle inputs keep their selection in internal state, so the search block has
  // to be remounted when those values are cleared from outside the inputs (pill / Clear button).
  const [vehicleFiltersKey, setVehicleFiltersKey] = useState(0);

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [vehicleDetailFilterEnabled, setVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const watchMake = watchFilters("make");
  const watchModel = watchFilters("model");
  const watchTrim = watchFilters("trim");
  const watchYearFrom = watchFilters("yearFrom");
  const watchYearTo = watchFilters("yearTo");
  const watchBodyStyle = watchFilters("bodyStyle");
  const watchNumberOfDoors = watchFilters("numberOfDoors");
  const watchFuelType = watchFilters("fuelType");
  const watchEngineSize = watchFilters("engineSize");
  const watchEngineCode = watchFilters("engineCode");
  const watchTransmission = watchFilters("transmissionType");
  const watchPartNumber = watchFilters("partNumber");
  const watchTagNumber = watchFilters("tagNumber");
  const watchVrm = watchFilters("vrm");
  const watchPartCategory = watchFilters("partCategory");
  const watchWarehouseLocation = watchFilters("warehouseLocation");
  const watchLocationSearchMode = watchFilters("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watchFilters("warehouseLocationBeginsWith");

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings", true),
      colHelper.display({
        id: "images",
        meta: {
          thClassName: "min-w-[12rem] w-52 !p-1.5",
          tdClassName: "min-w-[12rem] w-52 !p-1.5 align-top h-px",
        },
        cell: (info) => <UnlistedImageCell part={info.row.original} />,
      }),
      colHelper.accessor("categoryId", {
        header: "Part Information",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[28rem] max-w-[34rem] w-[30rem] !p-1.5",
          tdClassName: "min-w-[28rem] max-w-[34rem] w-[30rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <UnlistedPartTypeCell part={info.row.original} categories={allCategories.data?.data} showViewVehicle />
        ),
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        return offer?.buyItNowPrice ?? offer?.auctionStartPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Pricing",
        enableSorting: true,
        meta: {
          thClassName: "min-w-[11rem] w-[12rem] !p-1.5",
          tdClassName: "min-w-[11rem] w-[12rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Pricing
              </span>
              <PriceHistoryButton part={info.row.original} />
            </div>
            <div className="flex flex-1 flex-col px-2 pb-1.5">
              <UnlistedOfferPrice part={info.row.original} />
            </div>
          </div>
        ),
      }),
      colHelper.accessor((row) => computeListingScore(row).percent, {
        id: "recommendations",
        header: "eBay Snapshot",
        enableSorting: true,
        sortDescFirst: false,
        sortingFn: "basic",
        meta: {
          thClassName: "min-w-[26rem] w-[28rem] !p-1.5",
          tdClassName: "min-w-[26rem] w-[28rem] align-top h-px !p-1.5",
        },
        cell: (info) => <ListingRecommendationsCell part={info.row.original} />,
      }),
    ],
    [allCategories.data]
  );

  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const partIds = selected.map((row) => row.original.id);
    bulkUpdateProductDescription.mutate({ partIds, updateOffer: true }, bulkSaveHandlers);
  };

  const handleBulkWithdraw = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const offerIds = selected.map((row) => row.original.offers?.[0]?.id);
    bulkWithdraw.mutate({ offerIds }, bulkSaveHandlers);
  };

  const handleOpenRelistConfirm = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    setRelistRows(rows);
    setRelistAppendSku(false);
    setRelistConfirmOpen(true);
  };

  const handleBulkRelist = () => {
    const selected = relistRows?.rows.filter((row) => row.getIsSelected()) ?? [];

    setRelistConfirmOpen(false);
    setRelistRows(null);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const partIds = selected.map((row) => row.original.id);
    bulkRelist.mutate(
      { partIds, appendSku: relistAppendSku },
      {
        // The relist result carries a per-part breakdown of successes/failures with reasons,
        // so show that in a dedicated modal instead of a single generic toast.
        onSuccess: (response) => {
          setRelistResult(response.data);
          setRelistResultOpen(true);
        },
        onError: bulkSaveHandlers.onError,
      }
    );
  };

  const handleBulkUpdateTitle = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    // Compute each new title client-side, then queue the updates to run in the background.
    const items = selected
      .map((row) => {
        const part = row.original;
        const offer = part.offers?.at(0);
        if (!offer?.id) return null;
        return { offerId: offer.id, title: computeEbayListingTitle(part, null, true) };
      })
      .filter(Boolean) as { offerId: string; title: string }[];

    if (items.length === 0) {
      addNotification({ primaryText: "No listings with an offer selected", variant: "warn" });
      return;
    }

    updateTitles.mutate(
      { items },
      {
        onSuccess: (res) => {
          addNotification({
            primaryText: `Queued ${res?.data?.queued ?? items.length} title update${items.length === 1 ? "" : "s"} — status will update on each row`,
            variant: "success",
          });
          queryClient.invalidateQueries(["pagedTable"]);
        },
        onError: () => addNotification({ primaryText: "Failed to queue title updates", variant: "error" }),
      }
    );
  };

  const handleBulkUpdatePolicies = (rows: RowModel<IPart>, policiesData: IBulkUpdatePolicies) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const offerIds = selected
      .map((row) => row.original.offers?.at(0)?.id)
      .filter(Boolean) as string[];

    if (offerIds.length === 0) {
      addNotification({ primaryText: "No listings with an offer selected", variant: "warn" });
      return;
    }

    // Same policies applied to every selected offer, queued to run in the background.
    updatePolicies.mutate(
      {
        offerIds,
        locationKey: policiesData.locationKey,
        paymentPolicyId: policiesData.paymentPolicyId,
        fulfillmentPolicyId: policiesData.fulfillmentPolicyId,
        returnPolicyId: policiesData.returnPolicyId,
      },
      {
        onSuccess: (res) => {
          addNotification({
            primaryText: `Queued ${res?.data?.queued ?? offerIds.length} policy update${offerIds.length === 1 ? "" : "s"} — status will update on each row`,
            variant: "success",
          });
          queryClient.invalidateQueries(["pagedTable"]);
        },
        onError: () => addNotification({ primaryText: "Failed to queue policy updates", variant: "error" }),
      }
    );
  };

  const summarisePrices = (results: ISerpApiSearchResult[]) => {
    const prices = results
      .map((r) => r.priceExtracted)
      .filter((p): p is number => p != null && p > 0);

    if (prices.length === 0) return null;

    const round = (n: number) => Math.round(n * 100) / 100;
    return {
      min: round(Math.min(...prices)),
      max: round(Math.max(...prices)),
      avg: round(prices.reduce((sum, p) => sum + p, 0) / prices.length),
    };
  };

  // Writes intelligence results directly into all active pagedTable query caches so the
  // table updates immediately without waiting for a refetch
  const applyIntelligenceToCache = (
    partId: string,
    summary: { min: number; max: number; avg: number } | null,
    counts: { active: number; sold: number }
  ) => {
    queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
      queryClient.setQueryData(key, (current: any) => {
        if (!current?.data?.rows) return current;
        return {
          ...current,
          data: {
            ...current.data,
            rows: current.data.rows.map((row: any) =>
              row.id === partId
                ? {
                    ...row,
                    intelligenceMin: summary?.min ?? null,
                    intelligenceAverage: summary?.avg ?? null,
                    intelligenceMax: summary?.max ?? null,
                    intelligenceActiveCount: counts.active,
                    intelligenceSoldCount: counts.sold,
                    lastIntelligenceLookup: new Date().toISOString(),
                  }
                : row
            ),
          },
        };
      });
    });
  };

  // Patches BIN or auction start price in cache — used by both apply-pricing and undo
  const applyOfferPriceToCache = (partId: string, price: number, isAuction: boolean) => {
    queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
      queryClient.setQueryData(key, (current: any) => {
        if (!current?.data?.rows) return current;
        return {
          ...current,
          data: {
            ...current.data,
            rows: current.data.rows.map((row: any) => {
              if (row.id !== partId || !row.offers?.[0]) return row;
              const offer = { ...row.offers[0] };
              if (isAuction) offer.auctionStartPrice = price;
              else offer.buyItNowPrice = price;
              return { ...row, offers: [offer, ...row.offers.slice(1)] };
            }),
          },
        };
      });
    });
  };

  // Single bulk API call — the API handles chunked SerpApi requests (10 at a time) server-side
  // and returns per-part results so we can update the cache without a refetch
  const handleBulkRunIntelligence = async (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    setIntelligenceRunning(true);

    try {
      const partIds = selected.map((row) => row.original.id);
      const response = await intelligencePricingBulk.mutateAsync(partIds);
      const bulkResults: IBulkIntelligencePricingResult[] = response?.data ?? [];

      let succeeded = 0;
      let failed = 0;

      bulkResults.forEach((item) => {
        const results = item.results ?? [];
        const summary = summarisePrices(results);
        const counts = {
          active: results.filter((r) => !r.isSold).length,
          sold: results.filter((r) => r.isSold).length,
        };
        applyIntelligenceToCache(item.partId, summary, counts);
        if (results.length > 0) succeeded += 1;
        else failed += 1;
      });

      addNotification({
        primaryText: `Intelligence updated for ${succeeded} part${succeeded === 1 ? "" : "s"}${failed > 0 ? `, ${failed} returned no data` : ""}`,
        variant: failed > 0 && succeeded === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence pricing failed", variant: "error" });
    } finally {
      setIntelligenceRunning(false);
    }
  };

  // Sets BIN/auction price to intelligenceAverage. Stores the before/after in pricingUndo
  // so the user can revert with "Undo Pricing" without needing another API call
  const doApplyRecommendedPricing = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    const offerRequests: Record<string, IEbayOffer> = {};
    const changes: IPricingChange[] = [];
    let skipped = 0;

    selected.forEach((row) => {
      const part = row.original;
      const offer = part.offers?.at(0);
      const intel = part.intelligenceAverage;

      if (!offer || intel == null || intel <= 0) {
        skipped += 1;
        return;
      }

      const isAuction = offer.format === FormatType.Auction;
      const newPrice = Math.round(intel * 100) / 100;
      const oldPrice = (isAuction ? offer.auctionStartPrice : offer.buyItNowPrice) ?? 0;

      offerRequests[offer.id!] = {
        ...offer,
        ...(isAuction ? { auctionStartPrice: newPrice } : { buyItNowPrice: newPrice }),
      };
      changes.push({ partId: part.id, offer: { ...offer }, isAuction, oldPrice, newPrice });
    });

    if (changes.length === 0) {
      addNotification({
        primaryText: "No selected listings have recommended pricing — run Intelligence first",
        variant: "warn",
      });
      return;
    }

    bulkUpdateOffer.mutate(
      { offers: offerRequests },
      {
        ...bulkSaveHandlers,
        onSuccess: () => {
          bulkSaveHandlers.onSuccess();
          changes.forEach((change) =>
            applyOfferPriceToCache(change.partId, change.newPrice, change.isAuction)
          );
          setPricingUndo(changes);
          addNotification({
            primaryText: `Applied recommended pricing to ${changes.length} listing${changes.length === 1 ? "" : "s"}${skipped > 0 ? `, skipped ${skipped}` : ""}`,
            secondaryText: "Use 'Undo Pricing' to revert.",
            variant: "success",
          });
        },
      }
    );
  };

  const handleApplyRecommendedPricing = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    handleConfirm(
      () => doApplyRecommendedPricing(rows),
      "This will overwrite the Buy It Now price of the selected listings with the recommended (intelligence) price.\nListings without intelligence pricing are skipped. You can undo this immediately afterwards.\n\nApply recommended pricing?"
    );
  };

  const handleUndoPricing = () => {
    if (!pricingUndo || pricingUndo.length === 0) return;

    const offerRequests: Record<string, IEbayOffer> = {};
    pricingUndo.forEach((change) => {
      offerRequests[change.offer.id!] = change.offer;
    });

    const changes = pricingUndo;

    bulkUpdateOffer.mutate(
      { offers: offerRequests },
      {
        ...bulkSaveHandlers,
        onSuccess: () => {
          bulkSaveHandlers.onSuccess();
          changes.forEach((change) =>
            applyOfferPriceToCache(change.partId, change.oldPrice, change.isAuction)
          );
          setPricingUndo(null);
          addNotification({
            primaryText: `Reverted pricing on ${changes.length} listing${changes.length === 1 ? "" : "s"}`,
            variant: "success",
          });
        },
      }
    );
  };

  // deletedReason: 1 = scrap out (vs 0 = normal delete) — the API routes these differently
  const handleBulkScrapOut = async (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    try {
      await Promise.all(
        selected.map(async (row) => {
          await scrapOutPart.mutateAsync(row.original.id);
          row.toggleSelected(false);
        })
      );
      bulkSaveHandlers.onSuccess();
    } catch {
      bulkSaveHandlers.onError();
    }
  };

  const handleOpenQcModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());
    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setQcOpen(true);
  };

  const handleOpenBestOfferModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());
    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setBestOfferOpen(true);
  };

  const handleResetBestOffer = (bestOfferPercent: number, bestOfferUseCategoryThreshold: boolean) => {
    if (!selectedRows) return;

    const selected = selectedRows.rows.filter((row) => row.getIsSelected());

    // Send eligible offer ids only; the server recomputes prices and does the eBay work in the background.
    const offerIds: string[] = [];
    selected.forEach((row) => {
      const offer = row.original.offers?.at(0);
      if (offer?.id && offer.enableBestOffer && offer.buyItNowPrice && offer.buyItNowPrice > 0) {
        offerIds.push(offer.id);
      }
    });

    if (offerIds.length === 0) {
      addNotification({ primaryText: "No selected listings have Best Offer enabled", variant: "warn" });
      return;
    }

    resetBestOffer.mutate(
      { offerIds, percent: bestOfferPercent, useCategoryThreshold: bestOfferUseCategoryThreshold },
      {
        onSuccess: (res) => {
          const data = res?.data;

          if (!data?.batchId) {
            addNotification({ primaryText: res?.message || "Nothing to reset", variant: "warn" });
            return;
          }

          if (data.skipped > 0) {
            addNotification({
              primaryText: `Skipped ${data.skipped} listing${data.skipped === 1 ? "" : "s"} without Best Offer enabled`,
              variant: "info",
            });
          }

          addNotification({
            primaryText: `Queued ${data.queued} listing${data.queued === 1 ? "" : "s"} — status will update on each row`,
            variant: "success",
          });

          // Refetch so rows immediately show "Queued"; progress follows on the table's refetch.
          queryClient.invalidateQueries(["pagedTable"]);
        },
        onError: () => {
          addNotification({ primaryText: "Failed to queue Best Offer reset", variant: "error" });
        },
      }
    );

    setBestOfferOpen(false);
    setSelectedRows(null);
  };

  const handleConfirm = (onConfirm: () => void, message: string) => {
    setConfirmFunction(() => onConfirm);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const handleFinishedConfirm = () => {
    setConfirmOpen(false);
    setConfirmFunction(null);
    setConfirmMessage("");
  };

  const handleOpenPolicyModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());
    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setPolicyChangeOpen(true);
  };

  const handleOpenAspectsModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());
    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setAspectsChangeOpen(true);
  };

  const handleOpenSyncModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());
    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setSyncEbayOpen(true);
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  useEffect(() => {
    // Smart Match searches by the looked-up vehicle instead of the individual vehicle fields,
    // so the rest of the vehicle body is dropped while it is active.
    const vehiclePartSearchBody = isSmartMatch
      ? {
        smartSearch: watchVrm,
        categoryIds: watchPartCategory?.map((category) => category.id),
      }
      : buildVehiclePartSearchBody(getFilterValues(), vehicleDetailFilterEnabled);

    setAdditionalBody({
      ...vehiclePartSearchBody,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate ? toIsoDate(dates.startDate) : undefined,
      endDate: dates.endDate ? toIsoDate(dates.endDate) : undefined,
      dateFieldType: dateFieldType,
      missingFields: selectedMissingFields.length > 0 ? selectedMissingFields : undefined,
    });
  }, [getFilterValues, isSmartMatch, vehicleDetailFilterEnabled, watchMake, watchModel, watchTrim, watchYearFrom, watchYearTo, watchBodyStyle, watchNumberOfDoors, watchFuelType, watchEngineSize, watchEngineCode, watchTransmission, watchPartNumber, watchTagNumber, watchVrm, watchPartCategory, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType, selectedMissingFields]);

  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query => query.queryKey[0] === "pagedTable");
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching || fetchLoading,
    [queryState, isSearching, fetchLoading]
  );

  const handleClearFilters = () => {
    resetFilters(defaultFilterFormValues);
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setSorting(defaultSorting);
    setVehicleFiltersKey((key) => key + 1);
    selectedMissingFields.forEach((key) => onToggleMissingField?.(key));
  };

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onClear: () => void }[] = [];

    if (watchPartCategory?.length) {
      pills.push({
        key: "partCategory",
        label: `Categories: ${watchPartCategory.length}`,
        onClear: () => {
          setFilterValue("partCategory", [] as any);
          setVehicleFiltersKey((key) => key + 1);
        },
      });
    }

    if (watchWarehouseLocation?.id || watchWarehouseLocationBeginsWith) {
      pills.push({
        key: "warehouseLocation",
        label: `Location: ${watchWarehouseLocation?.displayName ?? watchWarehouseLocationBeginsWith}`,
        onClear: () => {
          setFilterValue("warehouseLocation", undefined as any);
          setFilterValue("warehouseLocationId", undefined as any);
          setFilterValue("warehouseLocationBeginsWith", "" as any);
        },
      });
    }

    if (dates.startDate || dates.endDate) {
      pills.push({
        key: "dates",
        label: `Dates: ${dateFieldType}`,
        onClear: () => setDates({ startDate: null, endDate: null }),
      });
    }

    if (onToggleMissingField) {
      selectedMissingFields.forEach((field) => {
        pills.push({
          key: `missing-${field}`,
          label: `Missing: ${MISSING_FIELD_OPTIONS.find((option) => option.key === field)?.label ?? field}`,
          onClear: () => onToggleMissingField(field),
        });
      });
    }

    return pills;
  }, [watchPartCategory, watchWarehouseLocation?.id, watchWarehouseLocation?.displayName, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, selectedMissingFields, onToggleMissingField, setFilterValue]);

  const getDefaultFilters = useCallback((): IListingsSavedDefaultFilters => ({
    locationSearchMode: watchLocationSearchMode,
    warehouseLocation: watchWarehouseLocation,
    warehouseLocationBeginsWith: watchWarehouseLocationBeginsWith,
    startDate: dates.startDate ? toIsoDate(dates.startDate) : null,
    endDate: dates.endDate ? toIsoDate(dates.endDate) : null,
    dateFieldType,
    sorting,
  }), [watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, sorting]);

  const applyDefaultFilters = useCallback((savedFilters: IListingsSavedDefaultFilters) => {
    setFilterValue("locationSearchMode", (savedFilters.locationSearchMode ?? "equals") as any);
    setFilterValue("warehouseLocation", savedFilters.warehouseLocation as any);
    setFilterValue("warehouseLocationId", savedFilters.warehouseLocation?.id as any);
    setFilterValue("warehouseLocationBeginsWith", (savedFilters.warehouseLocationBeginsWith ?? "") as any);

    setDates({
      startDate: savedFilters.startDate ? new Date(savedFilters.startDate) : null,
      endDate: savedFilters.endDate ? new Date(savedFilters.endDate) : null,
    });
    setDateFieldType(savedFilters.dateFieldType ?? "Created");
    if (savedFilters.sorting?.length) setSorting(savedFilters.sorting);
  }, [setFilterValue]);

  const expandedRow = (part: IPart) => {
    return <ListingExpandedRowContent part={part} />;
  };

  return (
    <>
      <ConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
        title={"Confirm changes to eBay listings?"}
        message={confirmMessage}
        confirmButtonText={"Confirm"}
        onConfirm={() => { confirmFunction?.(); handleFinishedConfirm(); }}
        isLoading={false}
      />

      <BulkUpdatePoliciesModal
        open={policyChangeOpen}
        onClose={() => { setPolicyChangeOpen(false); setSelectedRows(null); }}
        onSubmit={(policiesToUpdate) => { handleBulkUpdatePolicies(selectedRows!, policiesToUpdate); setPolicyChangeOpen(false); setSelectedRows(null); }}
        isLoading={updatePolicies.isLoading}
      />

      <Modal open={aspectsChangeOpen} setOpen={setAspectsChangeOpen} width="max-w-7xl">
        {selectedRows && (
          <BulkEditListingSpecifics
            parts={selectedRows.rows.filter((row) => row.getIsSelected()).map((row) => row.original)}
            onClose={() => { setAspectsChangeOpen(false); setSelectedRows(null); }}
          />
        )}
      </Modal>

      <Modal open={syncEbayOpen} setOpen={setSyncEbayOpen} width="max-w-7xl">
        {selectedRows && (
          <SyncEbayListingsModal
            parts={selectedRows.rows.filter((row) => row.getIsSelected()).map((row) => row.original)}
            onClose={() => { setSyncEbayOpen(false); setSelectedRows(null); }}
          />
        )}
      </Modal>

      <SendToQcModal
        open={qcOpen}
        setOpen={setQcOpen}
        parts={selectedRows?.rows.filter((row) => row.getIsSelected()).map((row) => row.original) ?? []}
        onCompleted={() => { setQcOpen(false); setSelectedRows(null); }}
        onFailed={() => { setQcOpen(false); setSelectedRows(null); }}
      />

      <BestOfferResetModal
        open={bestOfferOpen}
        onClose={() => setBestOfferOpen(false)}
        onConfirm={handleResetBestOffer}
        isLoading={resetBestOffer.isLoading}
      />

      <ConfirmModal open={relistConfirmOpen} setOpen={setRelistConfirmOpen}
        title={"Confirm changes to eBay listings?"}
        message={"Are you sure you want to relist the selected parts?\nThis will withdraw them from eBay then attempt to publish them."}
        confirmButtonText={"Confirm"}
        onConfirm={handleBulkRelist}
        isLoading={false}
      >
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={relistAppendSku}
            onChange={(e) => setRelistAppendSku(e.target.checked)}
          />
          <span>
            <span className="font-medium">Append SKU</span>
            <span className="block text-xs text-gray-500">
              Publish with a "-r1", "-r2", etc. suffix on the SKU to improve marketplace visibility. Tag numbers and scanning are unaffected.
            </span>
          </span>
        </label>
      </ConfirmModal>

      <BulkRelistResultModal
        open={relistResultOpen}
        setOpen={setRelistResultOpen}
        result={relistResult}
      />

      <form className="my-2 mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <VehiclePartSearchFilters
          key={vehicleFiltersKey}
          register={registerFilters}
          setValue={setFilterValue}
          getValues={getFilterValues}
          watch={watchFilters}
          categories={allCategories.data?.data ?? []}
          categoriesLoading={allCategories.isLoading}
          disabled={isSearchDisabled}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          isSmartMatch={isSmartMatch}
          setIsSmartMatch={setIsSmartMatch}
          vehicleDetailFilterEnabled={vehicleDetailFilterEnabled}
          setVehicleDetailFilterEnabled={setVehicleDetailFilterEnabled}
          onApplySmartSearch={setAdditionalBody}
        />

        <hr className="mt-1" />

        <TableFilterPanel
          activeFilterPills={activeFilterPills}
          disabled={isSearchDisabled}
          defaultFiltersStorageKey="wilma:tableFilters:ebay-listings"
          getDefaultFilters={getDefaultFilters}
          onApplyDefaultFilters={applyDefaultFilters}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <WarehouseLocationSearch
              showSearchOptions={true}
              register={registerFilters}
              setValue={setFilterValue}
              watch={watchFilters}
              isSearchDisabled={isSearchDisabled}
              warehouseLocations={warehouseLocations}
            />

            <DateRangeFilter
              dates={dates}
              onDatesChange={handleDatesChange}
              dateFieldType={dateFieldType}
              onDateFieldTypeChange={setDateFieldType}
              disabled={isSearchDisabled}
            />

            <TableDateSortControl
              sorting={sorting}
              onSortingChange={setSorting}
              disabled={isSearchDisabled}
            />

            {onToggleMissingField && (
              <div className="flex flex-col">
                <span className="text-sm font-medium text-black">Missing</span>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {MISSING_FIELD_OPTIONS.map((option) => {
                    const active = selectedMissingFields.includes(option.key);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        disabled={isSearchDisabled}
                        onClick={() => onToggleMissingField(option.key)}
                        className={classNames(
                          "rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
                          active
                            ? "border-primary-600 bg-primary-600 text-white"
                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TableFilterPanel>

        <ButtonInput
          label="Clear"
          classes={classNames(isSearchDisabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
          onClick={handleClearFilters}
          disabled={isSearchDisabled}
        />
      </form>

      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Listed}
        columns={cols}
        queryLabel="Search part details"
        showTotal={true}
        additionalBody={additionalBody}
        sorting={sorting}
        onSortingChange={setSorting}
        extraRow={(part: IPart) => (
          <div className="flex flex-col">
            <UnlistedEbayTitle part={part} />
            <WilmaTitleRow part={part} />
          </div>
        )}
        extraRowPosition="top"
        expandedRow={expandedRow}
        actionButtons={[
          // The update actions live behind one toggle. The irreversible marketplace actions
          // (relist / withdraw / scrap) stay outside it, as does the transient Undo Pricing
          // button so it can be reached without opening the group.
          {
            label: "Bulk Actions",
            onClick: () => { },
            groupedButtons: [
              { label: "Run Intelligence", onClick: (e: RowModel<IPart>) => handleBulkRunIntelligence(e), isLoading: intelligenceRunning, disabledOnNoRows: true },
              { label: "Apply Recommended Pricing", onClick: (e: RowModel<IPart>) => handleApplyRecommendedPricing(e), isLoading: bulkUpdateOffer.isLoading, disabledOnNoRows: true },
              { label: "Update Aspects", onClick: (e: RowModel<IPart>) => handleOpenAspectsModal(e), disabledOnNoRows: true },
              { label: "Update Titles", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkUpdateTitle(e), "Are you sure you want to update the titles of the selected parts?"), isLoading: updateTitles.isLoading, disabledOnNoRows: true },
              { label: "Update Descriptions", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkResetDescription(e), "Are you sure you want to reset the description of the selected parts?"), isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
              { label: "Update Policies", onClick: (e: RowModel<IPart>) => handleOpenPolicyModal(e), isLoading: updatePolicies.isLoading, disabledOnNoRows: true },
              { label: "Reset Best Offer", onClick: (e: RowModel<IPart>) => handleOpenBestOfferModal(e), disabledOnNoRows: true },
              { label: "Send to QC", onClick: (e: RowModel<IPart>) => handleOpenQcModal(e), disabledOnNoRows: true },
              { label: "Sync eBay", onClick: (e: RowModel<IPart>) => handleOpenSyncModal(e), disabledOnNoRows: true },
            ],
          },
          ...(pricingUndo && pricingUndo.length > 0
            ? [{ label: `Undo Pricing (${pricingUndo.length})`, onClick: () => handleUndoPricing(), isLoading: bulkUpdateOffer.isLoading }]
            : []),
          { label: "Bulk Relist", onClick: (e: RowModel<IPart>) => handleOpenRelistConfirm(e), isLoading: bulkRelist.isLoading, disabledOnNoRows: true },
          { label: "Bulk Withdraw", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkWithdraw(e), "Are you sure you want to withdraw the selected parts?"), isLoading: bulkWithdraw.isLoading, disabledOnNoRows: true },
          { label: "Scrap Out", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkScrapOut(e), "Are you sure you want to scrap out the selected parts?\nThis will remove them from eBay and mark them as scrapped."), isLoading: scrapOutPart.isLoading, disabledOnNoRows: true },
        ]}
      />
    </>
  );
}
